import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Heart, ArrowUpRight } from 'lucide-react';
import { bookmarkStart } from '../../redux/user/Action';
import { getRequiredProtectedData } from '../../redux/apicalltemplate';
import { userApis } from '../../redux/api';
import CampaignArtwork from './CampaignArtwork';
import {
  campaignMetrics,
  campaignStatus,
  formatUsd,
  formatXlm,
} from './campaignPresentation';
import './InvestmentCard.css';

export default function MusicInvestmentCard({
  el,
  handleCrowdsaleDetails,
  handleInvest,
  liveprice,
  type = 'crowdsale',
  ismycrowdsale = false,
  userid = '',
  source = '',
  isBookmark = false,
  compact = false,
}) {
  const history = useHistory();
  const dispatch = useDispatch();
  const [saved, setSaved] = useState(isBookmark);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    setSaved(isBookmark);
  }, [isBookmark]);
  const title = el?.musicId?.longName || el?.name || 'Untitled music';
  const artist = el?.featured_by || el?.musicId?.shareHolderName;
  const offeringType =
    el?.type === 'newSong'
      ? 'crowdfund'
      : el?.type === 'songOffering'
      ? 'crowdsale'
      : source === 'search' && el?.musicId?.musicType === 'new'
      ? 'crowdfund'
      : type;
  const metrics = campaignMetrics(el, liveprice, ismycrowdsale);
  const status = campaignStatus(el, metrics.soldOut);
  const issuer = typeof el?.isuuer === 'object' ? el.isuuer?._id : el?.isuuer;
  const canInvest =
    !ismycrowdsale &&
    status.canInvest &&
    (!userid || String(userid) !== String(issuer));
  const details = () => handleCrowdsaleDetails(el);
  const handleLike = async () => {
    if (!localStorage.getItem('token')) {
      history.push('/login');
      return;
    }
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      const response = await getRequiredProtectedData(
        'PUT',
        `${!saved ? userApis.BOOKMARK : userApis.UNBOOKMARK}/${el._id}`,
        {},
        {}
      );
      if (!response?.data || response.data.success === false)
        throw new Error('Unable to update your saved offerings. Try again.');
      setSaved((previous) => !previous);
      dispatch(bookmarkStart({}));
    } catch {
      setError('Unable to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <article
      className={`tm-investment-card ${
        compact ? 'tm-investment-card--compact' : ''
      }`}
    >
      <div className="tm-card-media">
        <button
          className="tm-artwork-button"
          onClick={details}
          aria-label={`View ${title}`}
        >
          <CampaignArtwork src={el?.musicId?.bitMapImage} title={title} />
        </button>
        {!compact && (
          <>
            <span className="tm-offering-badge">
              {offeringType === 'crowdfund' ? 'Crowdfunding' : 'Offering'}
            </span>
            <button
              className={`tm-favorite ${saved ? 'is-saved' : ''}`}
              onClick={handleLike}
              disabled={saving}
              aria-label={`${saved ? 'Unsave' : 'Save'} ${title}`}
              aria-pressed={saved}
            >
              <Heart
                size={18}
                fill={saved ? 'currentColor' : 'none'}
                aria-hidden="true"
              />
            </button>
          </>
        )}
      </div>
      <div className="tm-card-body">
        <h3>
          <button onClick={details} title={title}>
            {title}
          </button>
        </h3>
        <div className="tm-card-identity">
          {artist && (
            <p className="tm-card-artist" title={artist}>
              {artist}
            </p>
          )}
          {!compact && status.label && (
            <span
              className={`tm-status ${
                status.canInvest ? 'tm-status--live' : ''
              }`}
            >
              {status.label}
            </span>
          )}
        </div>
        {!compact && !ismycrowdsale && (
          <div className="tm-card-funding">
            <div className="tm-funding-label">
              <span className="sr-only">USD funding progress</span>
              {metrics.progress !== null ? (
                <div
                  className="tm-funding-track"
                  role="progressbar"
                  aria-label={`${title} funding`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.min(100, metrics.progress)}
                >
                  <span
                    style={{ width: `${Math.min(100, metrics.progress)}%` }}
                  />
                </div>
              ) : (
                <span
                  className="tm-funding-track tm-funding-track--unknown"
                  aria-hidden="true"
                />
              )}
              <strong>
                {metrics.progress !== null
                  ? `${new Intl.NumberFormat('en-US', {
                      maximumFractionDigits: 1,
                    }).format(metrics.progress)}%`
                  : 'Unavailable'}
              </strong>
            </div>
            <dl className="tm-funding-values">
              <div>
                <dt>Raised · USD</dt>
                <dd
                  title={
                    metrics.raised !== null
                      ? formatUsd(metrics.raised)
                      : undefined
                  }
                >
                  {metrics.raised !== null ? formatUsd(metrics.raised) : '—'}
                </dd>
              </div>
              <div>
                <dt>Target · USD</dt>
                <dd
                  title={
                    metrics.target > 0 ? formatUsd(metrics.target) : undefined
                  }
                >
                  {metrics.target > 0 ? formatUsd(metrics.target) : '—'}
                </dd>
              </div>
            </dl>
            {(metrics.xlm !== null || metrics.targetXlm > 0) && (
              <div className="tm-xlm-equivalent" aria-label="XLM equivalents">
                <span
                  title={
                    metrics.xlm !== null
                      ? `${formatXlm(metrics.xlm)} XLM raised`
                      : undefined
                  }
                >
                  <span className="sr-only">Raised: </span>
                  {metrics.xlm !== null && (
                    <>
                      <strong>{formatXlm(metrics.xlm)}</strong> XLM
                    </>
                  )}
                </span>
                <span
                  title={
                    metrics.targetXlm > 0
                      ? `${formatXlm(metrics.targetXlm)} XLM target`
                      : undefined
                  }
                >
                  <span className="sr-only">Target: </span>
                  {metrics.targetXlm > 0 && (
                    <>
                      <strong>{formatXlm(metrics.targetXlm)}</strong> XLM
                    </>
                  )}
                </span>
              </div>
            )}
          </div>
        )}
        {error && (
          <p className="tm-card-error" role="alert">
            {error}
          </p>
        )}
      </div>
      {!compact && (
        <div className="tm-card-action">
          <button
            className={canInvest ? 'tm-invest-button' : 'tm-details-button'}
            onClick={() =>
              canInvest
                ? localStorage.getItem('token')
                  ? handleInvest(el, offeringType)
                  : history.push('/login')
                : details()
            }
          >
            {canInvest ? 'Invest now' : 'View details'}
            <ArrowUpRight size={16} aria-hidden="true" />
          </button>
        </div>
      )}
    </article>
  );
}
export function InvestmentCardSkeleton() {
  return (
    <div className="tm-investment-card tm-card-skeleton" aria-hidden="true">
      <div className="tm-campaign-artwork" />
      <div className="tm-card-body">
        <i />
        <i />
        <i />
      </div>
      <div className="tm-card-action">
        <i />
      </div>
    </div>
  );
}
